import React, { useState, useRef, useEffect } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import { Card, CardContent } from '../ui/card.tsx';
import { Button } from '../ui/button.tsx';
import { Slider } from '../ui/slider.tsx';
import * as THREE from 'three';
import { DXFParser } from './DXFParser.tsx';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface PergolaProps {
  width: number;
  length: number;
  height: number;
  wallPositions?: THREE.Vector3[];
  postWidth?: number;
}

interface EnvironmentProps {
  geometry: THREE.BufferGeometry | null;
  onWallsLoaded?: (positions: THREE.Vector3[]) => void;
}

interface Dimensions {
  width: number;
  length: number;
  height: number;
}

// Environment component to handle DXF walls
const Environment: React.FC<EnvironmentProps> = ({ geometry, onWallsLoaded }) => {
  const [walls, setWalls] = useState<THREE.BufferGeometry | null>(null);
  
  useEffect(() => {
    if (geometry) {
      setWalls(geometry);
      if (onWallsLoaded) {
        const positions = geometry.attributes.position.array;
        const wallData: THREE.Vector3[] = [];
        for (let i = 0; i < positions.length; i += 9) {
          const center = new THREE.Vector3(
            (positions[i] + positions[i + 3] + positions[i + 6]) / 3,
            (positions[i + 1] + positions[i + 4] + positions[i + 7]) / 3,
            (positions[i + 2] + positions[i + 5] + positions[i + 8]) / 3
          );
          wallData.push(center);
        }
        onWallsLoaded(wallData);
      }
    }
  }, [geometry, onWallsLoaded]);

  if (!walls) return null;

  return (
    <mesh geometry={walls}>
      <meshStandardMaterial 
        color="#e0e0e0" 
        side={THREE.DoubleSide}
        roughness={0.9}
      />
    </mesh>
  );
};

// Pergola component
const Pergola: React.FC<PergolaProps> = ({ 
  width, 
  length, 
  height, 
  wallPositions = [], 
  postWidth = 0.15 
}) => {
  const [wallMounted, setWallMounted] = useState(false);
  const [mountedWallPosition, setMountedWallPosition] = useState<THREE.Vector3 | null>(null);
  
  useEffect(() => {
    if (wallPositions.length > 0) {
      const pergolaCenter = new THREE.Vector3(0, height/2, 0);
      let nearestWall: THREE.Vector3 | null = null;
      let minDistance = Infinity;
      
      wallPositions.forEach(wallPos => {
        const distance = pergolaCenter.distanceTo(wallPos);
        if (distance < minDistance && distance < 1) {
          minDistance = distance;
          nearestWall = wallPos;
        }
      });
      
      setWallMounted(!!nearestWall);
      setMountedWallPosition(nearestWall);
    }
  }, [wallPositions, height]);

  const halfWidth = width / 2;
  const halfLength = length / 2;
  
  const position = wallMounted && mountedWallPosition ? 
    [mountedWallPosition.x, 0, mountedWallPosition.z] : [0, 0, 0];
  
  return (
    <group position={position as [number, number, number]}>
      {!wallMounted && (
        <>
          <mesh position={[-halfWidth, height/2, -halfLength]} castShadow>
            <boxGeometry args={[postWidth, height, postWidth]} />
            <meshStandardMaterial color="#8B4513" />
          </mesh>
          <mesh position={[halfWidth, height/2, -halfLength]} castShadow>
            <boxGeometry args={[postWidth, height, postWidth]} />
            <meshStandardMaterial color="#8B4513" />
          </mesh>
        </>
      )}
      <mesh position={[-halfWidth, height/2, halfLength]} castShadow>
        <boxGeometry args={[postWidth, height, postWidth]} />
        <meshStandardMaterial color="#8B4513" />
      </mesh>
      <mesh position={[halfWidth, height/2, halfLength]} castShadow>
        <boxGeometry args={[postWidth, height, postWidth]} />
        <meshStandardMaterial color="#8B4513" />
      </mesh>
      <mesh position={[0, height, 0]} castShadow>
        <boxGeometry args={[width + postWidth, postWidth, length + postWidth]} />
        <meshStandardMaterial color="#A0522D" />
      </mesh>
    </group>
  );
};

const PergolaViewer: React.FC<{ fullScreen?: boolean, initialDxfContent?: string }> = ({ fullScreen = false, initialDxfContent = '' }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [dimensions, setDimensions] = useState<Dimensions>({
    width: 4,
    length: 3,
    height: 2.5
  });
  
  const [wallGeometry, setWallGeometry] = useState<THREE.BufferGeometry | null>(null);
  const [wallPositions, setWallPositions] = useState<THREE.Vector3[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (initialDxfContent) {
      const geometry = new DXFParser().parse(initialDxfContent);
      setWallGeometry(geometry);
    }
  }, [initialDxfContent]);
  useEffect(() => {
    const loadDefaultDXF = async () => {
      try {
        const response = await fetch('/default-walls.dxf');
        const text = await response.text();
        const parser = new DXFParser();
        const geometry = parser.parse(text);
        setWallGeometry(geometry);
      } catch (error) {
        console.error('Error loading default DXF:', error);
      }
    };
    
    loadDefaultDXF();
  }, []);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        const geometry = await DXFParser.loadDXF(file);
        setWallGeometry(geometry);
      } catch (error) {
        console.error('Error loading DXF file:', error);
      }
    }
  };

  return (
    <div className={`${fullScreen ? 'h-screen' : 'h-full'} w-full relative`}>
      {fullScreen && (
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 z-10 px-4 py-2 bg-white rounded-md shadow-md hover:bg-gray-100 flex items-center gap-2"
        >
          <span>←</span> {t('pergolaViewer.back')}
        </button>
      )}
      
      <div className="flex h-full">
        <div className="flex-grow relative">
          <Canvas shadows camera={{ position: [5, 5, 5], fov: 50 }}>
          <ambientLight intensity={0.5} />
          <directionalLight
            position={[10, 10, 5]}
            intensity={1}
            castShadow
            shadow-mapSize-width={2048}
            shadow-mapSize-height={2048}
          />
          
          <OrbitControls makeDefault />
          
          <Environment 
            geometry={wallGeometry} 
            onWallsLoaded={setWallPositions}
          />
          
          <Pergola 
            {...dimensions} 
            wallPositions={wallPositions}
          />
        </Canvas>
      </div>

      <Card className={`${
          fullScreen 
            ? 'absolute right-0 top-0 h-full bg-white/90 backdrop-blur-sm' 
            : 'h-full'
          } w-64 overflow-y-auto`}
        >
          <CardContent className="p-4 space-y-4">
            <div>
              <h3 className="mb-2">{t('pergolaViewer.dimensions.width', { value: dimensions.width })}</h3>
              <Slider 
                value={[dimensions.width]}
                min={2}
                max={6}
                step={0.1}
                onValueChange={([value]) => setDimensions(prev => ({ ...prev, width: value }))}
              />
            </div>

            <div>
              <h3 className="mb-2">{t('pergolaViewer.dimensions.length', { value: dimensions.length })}</h3>
              <Slider 
                value={[dimensions.length]}
                min={2}
                max={6}
                step={0.1}
                onValueChange={([value]) => setDimensions(prev => ({ ...prev, length: value }))}
              />
            </div>

            <div>
              <h3 className="mb-2">{t('pergolaViewer.dimensions.height', { value: dimensions.height })}</h3>
              <Slider 
                value={[dimensions.height]}
                min={2}
                max={4}
                step={0.1}
                onValueChange={([value]) => setDimensions(prev => ({ ...prev, height: value }))}
              />
            </div>

            <div className="space-y-2">
              <h3 className="font-medium mb-2">{t('pergolaViewer.environment.title')}</h3>
              <input
                type="file"
                accept=".dxf"
                className="hidden"
                ref={fileInputRef}
                onChange={handleFileUpload}
              />
              <Button 
                onClick={() => fileInputRef.current?.click()}
                variant="outline"
                className="w-full"
              >
                {t('pergolaViewer.environment.uploadButton')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PergolaViewer;